import axios from 'axios';
import { loaderService } from './loaderService';
import {
  formatDocumentsInPayload,
  normalizeDocumentsInPayload,
  normalizeDocumentInUrl,
} from '../utils/documentFormat';

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

let activeRequests = 0;

const showLoader = () => {
  if (activeRequests === 0) {
    loaderService.showLoader();
  }
  activeRequests++;
};

const hideLoader = () => {
  activeRequests--;
  if (activeRequests <= 0) {
    activeRequests = 0;
    loaderService.hideLoader();
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) =>
    error ? prom.reject(error) : prom.resolve(token),
  );
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    if (!config.skipLoader) {
      showLoader();
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.url) {
      config.url = normalizeDocumentInUrl(config.url);
    }

    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = normalizeDocumentsInPayload(config.data);
    }

    return config;
  },
  (error) => {
    if (!error.config?.skipLoader) {
      hideLoader();
    }
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    if (!response.config.skipLoader) {
      hideLoader();
    }
    if (response.data != null) {
      response.data = formatDocumentsInPayload(response.data);
    }
    return response;
  },
  async (error) => {
    try {
      const originalRequest = error.config;

      if (
        error.response?.status === 403 &&
        !originalRequest.url.includes('/auth/')
      ) {
        if (!window.location.pathname.includes('/admin/unauthorized')) {
          window.location.href = '/admin/unauthorized';
        }
        return Promise.reject(error);
      }

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes('/auth/refresh')
      ) {
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          clearAuth();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return api(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/auth/refresh`,
            { refreshToken },
          );

          const newAccessToken = response.data.payload.token;

          localStorage.setItem('token', newAccessToken);
          api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (err) {
          processQueue(err, null);
          clearAuth();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    } finally {
      if (!error.config?.skipLoader) {
        hideLoader();
      }
    }
  },
);

function clearAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('isAuthenticated');
  sessionStorage.setItem('sessionExpired', 'true');
  window.location.href = '/auth/sign-in';
}

// Función para crear inscripciones de cursos
export const createCourseInscription = async (inscriptionData) => {
  try {
    const response = await api.post('/course-inscriptions', inscriptionData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Función para obtener cursos de un estudiante
export const getStudentCourses = async (studentId) => {
  try {
    const response = await api.get(`/course-inscriptions/student/${studentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Servicios para students y subjects (necesarios para revalidations)
export const personService = {
  get: async (url) => {
    try {
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const subjectService = {
  get: async (url) => {
    try {
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Función para obtener todas las asignaturas
export const getAllSubjects = async () => {
  try {
    const response = await api.get('/subjects');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Función para obtener todos los empleados
export const getAllEmployees = async () => {
  try {
    const response = await api.get('/employees');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Empleado por id (trae fechas de ingreso/egreso completas). */
export const getEmployeeById = async (id) => {
  try {
    const response = await api.get(`/employees/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Registrar egreso/baja de un empleado
export const terminateEmployee = async (id, data) => {
  try {
    const response = await api.patch(`/employees/${id}/terminate`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;
